import type { FC, ReactNode } from 'react'
import type { FooterLink } from '@/types'

import Image from 'next/image'
import { Facebook, Instagram, Youtube } from 'lucide-react'
import { useSiteContent } from '@/lib/site-content'
import CmsText from '@/components/generals/cms-text'
import LogoLink from '@/components/generals/logo-link'

const Footer: FC = (): ReactNode => {
    const { settings, footerLinks, year } = useSiteContent()

    const projectLinks = footerLinks.filter(link => link.footer_column === 'projekti')
    const postLinks = footerLinks.filter(link => link.footer_column === 'postet')

    return (
        <footer id='footer' className='bg-brand-red text-white'>
            <div className='container-page grid grid-cols-2 gap-10 pb-12 pt-16 md:grid-cols-4 md:gap-8'>
                <div>
                    <LogoLink>
                        <Image
                            src='/logo-white.svg'
                            alt='Dhurata e Bajramit'
                            width={303}
                            height={61}
                            className='h-10 w-auto'
                        />
                    </LogoLink>

                    <p
                        className='mt-4 max-w-xs text-sm text-white/75'
                        data-mdlfy-cms={`site_settings:${settings.id}:site_description`}
                    >
                        {settings.site_description}
                    </p>
                </div>

                <FooterColumn headingKey='footer.columns.projekti' links={projectLinks} />

                <FooterColumn headingKey='footer.columns.postet' links={postLinks} />

                <div>
                    <CmsText as='h4' textKey='footer.columns.social' className='font-display text-base font-bold' />

                    <ul className='mt-3 flex items-center gap-3'>
                        {settings.facebook_url ? (
                            <SocialIcon href={settings.facebook_url} label='Facebook'>
                                <Facebook className='h-4 w-4' />
                            </SocialIcon>
                        ) : null}

                        {settings.instagram_url ? (
                            <SocialIcon href={settings.instagram_url} label='Instagram'>
                                <Instagram className='h-4 w-4' />
                            </SocialIcon>
                        ) : null}

                        {settings.youtube_url ? (
                            <SocialIcon href={settings.youtube_url} label='Youtube'>
                                <Youtube className='h-4 w-4' />
                            </SocialIcon>
                        ) : null}
                    </ul>
                </div>
            </div>

            <div className='border-t border-white/15'>
                <div className='container-page flex flex-col items-center justify-between gap-3 py-6 text-xs text-white/70 md:flex-row'>
                    <p>
                        © {year} <CmsText textKey='footer.copyright' />
                    </p>

                    <a
                        href={`mailto:${settings.contact_email}`}
                        className='hover:text-white'
                        data-mdlfy-cms={`site_settings:${settings.id}:contact_email`}
                    >
                        {settings.contact_email}
                    </a>
                </div>
            </div>
        </footer>
    )
}

interface FooterColumnProps {
    headingKey: string
    links: FooterLink[]
}

const FooterColumn: FC<FooterColumnProps> = ({ headingKey, links }): ReactNode => {
    return (
        <div>
            <CmsText as='h4' textKey={headingKey} className='font-display text-base font-bold' />

            <ul className='mt-3 space-y-2 text-sm text-white/80'>
                {links.map(link => (
                    <li key={link.id}>
                        <a
                            href={link.href}
                            className='transition-colors hover:text-white'
                            data-mdlfy-cms={`footer_links:${link.id}:label`}
                        >
                            {link.label}
                        </a>
                    </li>
                ))}
            </ul>
        </div>
    )
}

interface SocialIconProps {
    href: string
    label: string
    children: ReactNode
}

const SocialIcon: FC<SocialIconProps> = ({ href, label, children }): ReactNode => {
    return (
        <li>
            <a
                href={href}
                target='_blank'
                rel='noreferrer noopener'
                aria-label={label}
                className='inline-flex h-9 w-9 items-center justify-center rounded-pill bg-white/10 transition-colors hover:bg-white/20'
            >
                {children}
            </a>
        </li>
    )
}

export default Footer
